/**
 * Shared one-shot fetch retry for PostgREST PGRST303 ("JWT issued at future").
 *
 * Composed inside `SupabaseService.initialize` so portals, Admin MCP shared-data
 * handlers, and edge workers that call `initializeSupabaseService()` inherit the
 * same short delay + single retry (LEA-295).
 */
import { POSTGREST_JWT_ISSUED_AT_FUTURE_CODE } from './errors.utils.js';
const DEFAULT_DELAY_MS = 1000;
/**
 * Returns true when the response is an HTTP 401 whose JSON body has
 * `code === 'PGRST303'`. Probes via `response.clone()` so the original body
 * stream stays unread for supabase-js. Never parses successful (`ok`) bodies.
 */
export async function isPgrst303Unauthorized(response) {
    if (response.status !== 401) {
        return false;
    }
    try {
        const body = await response.clone().json();
        return (!!body &&
            typeof body === 'object' &&
            body.code === POSTGREST_JWT_ISSUED_AT_FUTURE_CODE);
    }
    catch (_a) {
        return false;
    }
}
/**
 * Whether `input` can safely be passed to a second `fetch` call.
 * supabase-js PostgREST uses URL string + re-readable `init.body`. If `input`
 * is a `Request` whose body stream cannot be restarted, skip retry.
 */
function canRetryFetchInput(input) {
    if (typeof input === 'string' || input instanceof URL) {
        return true;
    }
    if (typeof Request !== 'undefined' && input instanceof Request) {
        return input.body == null || input.bodyUsed === false;
    }
    return true;
}
/**
 * Wraps `baseFetch` with at most one automatic retry when PostgREST returns
 * PGRST303 on HTTP 401.
 */
export function createPgrst303RetryFetch(baseFetch, options) {
    var _a, _b;
    const delayMs = (_a = options === null || options === void 0 ? void 0 : options.delayMs) !== null && _a !== void 0 ? _a : DEFAULT_DELAY_MS;
    const sleep = (_b = options === null || options === void 0 ? void 0 : options.sleep) !== null && _b !== void 0 ? _b : ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
    return async (input, init) => {
        const first = await baseFetch(input, init);
        // `first` body must remain unread for the non-retry path (clone-only probe).
        if (!(await isPgrst303Unauthorized(first))) {
            return first;
        }
        if (!canRetryFetchInput(input)) {
            return first;
        }
        await sleep(delayMs);
        return baseFetch(input, init);
    };
}
